import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {catchError, filter, takeUntil} from 'rxjs/operators';

import {environment} from '@env/environment';
import {BaseAPIService} from '@app/core/service/base-api.service';
import {
    PII_LEVEL,
    PIIShieldApplicationLevel,
    PIIShieldLevelConfig,
    PIIShieldLevelModel
} from '@app/views/admin/application-settings/components/basic/pii-shield-level-info/pii-shield.model';
import {
    PIIShieldEntityAttributes, PIIShieldAttribute
} from '@app/views/admin/application-settings/components/basic/pii-shield-level-info/entity-attributes/pii-shield-entities-attributes.component';
import {Integration} from '@app/views/admin/application-settings/models/integration.model';
import {DataSourceType} from '@app/views/admin/application-settings/models/data-source-type.model';
import {DataAttribute} from '@app/views/admin/application-settings/models/data-attribute.model';
import {IntegrationTask} from '@app/views/admin/application-settings/models/integration-task.model';
import {IntegrationTaskMap} from '@app/views/admin/application-settings/models/integration-task-map.model';
import {PIIShieldAction} from '@app/core/model/integration-models';
import {select, Store} from '@ngrx/store';
import {isAuthenticated, State} from '@app/app.reducers';
import {HttpClient} from '@angular/common/http';
import {MESSAGES} from '@app/core/model/messages';

@Injectable()

export class PIIShieldService extends BaseAPIService {

    private readonly GET_PII_SHIELD_CONFIG_ERROR_MESSAGE = 'Could not load Privacy Shield config.';

    constructor(http: HttpClient,
                private store: Store<State>) {
        super(http);
    }

    static processPIILevelConfig(piiShieldLevelsConfig: PIIShieldLevelConfig[], integration: Integration): PIIShieldEntityAttributes[] {
        const entityAttributesList = <PIIShieldEntityAttributes[]>[];
        const eduToneDSEntitiesAttributesObj: { [key: string]: { [key: string]: DataAttribute } } =
            PIIShieldService.getEduToneDSEntitiesAttributesObj(integration);

        piiShieldLevelsConfig.forEach((piiShieldLevelConfig: PIIShieldLevelConfig) => {
            const eduToneDSEntityAttributesObj = eduToneDSEntitiesAttributesObj[piiShieldLevelConfig.rosterType] || {};
            const entityAttributes = new PIIShieldEntityAttributes(piiShieldLevelConfig.rosterType);
            const entityAttributesObj = {};

            piiShieldLevelConfig.levelModels.sort((levelModel1: PIIShieldLevelModel, levelModel2: PIIShieldLevelModel) =>
                levelModel1.piiLevel.toLowerCase().localeCompare(levelModel2.piiLevel.toLowerCase())
            );

            piiShieldLevelConfig.levelModels.forEach((levelModel: PIIShieldLevelModel) => {
                levelModel.maskedFields.forEach((attr: string) => {
                    const attribute = eduToneDSEntityAttributesObj[attr];
                    if (attribute && !entityAttributesObj[attr]) {
                        const piiShieldAttribute = new PIIShieldAttribute(
                            attribute.propertyName,
                            attribute.friendlyName,
                            levelModel.piiLevel
                        );
                        entityAttributes.attributes.push(piiShieldAttribute);
                        entityAttributesObj[attr] = piiShieldAttribute;
                    }
                });
            });

            if (entityAttributes?.attributes?.length > 0) {
                entityAttributesList.push(entityAttributes);
            }
        });

        return entityAttributesList;
    }

    static getEduToneDSEntitiesAttributesObj(integration: Integration): { [key: string]: { [key: string]: DataAttribute } } {
        const eduToneDS: string = (integration.source.dataSourceType === DataSourceType.EduTone) ? 'source' : 'target';

        const eduToneDSEntitiesAttributesObj = {};

        integration.tasks.forEach((task: IntegrationTask) => {
            let eduToneDSEntityAttributesObj = {};
            eduToneDSEntitiesAttributesObj[task[`${eduToneDS}Type`]] = eduToneDSEntityAttributesObj;
            if (!eduToneDSEntityAttributesObj) {
                eduToneDSEntityAttributesObj = {}; // { attribute.propertyName : DataAttribute }
            }
            task[`${eduToneDS}AttributeList`].forEach((attribute: DataAttribute) =>
                eduToneDSEntityAttributesObj[attribute.propertyName] = attribute
            );
        });

        return eduToneDSEntitiesAttributesObj;
    }

    static setPIIShieldConfigEntitiesAttributesObj(
        piiShieldConfigEntitiesAttributes: PIIShieldEntityAttributes[],
        integrationTasks: IntegrationTask[],
        eduToneDS: 'source' | 'target'
    ): [object, PIIShieldEntityAttributes[]] {
        const piiShieldConfigEntitiesAttributesObj = {};  // { edutoneEntityType: {[propertyName]: IntegrationTaskMap} }
        piiShieldConfigEntitiesAttributes.forEach((piiShieldEntityAttributes: PIIShieldEntityAttributes) => {

            const piiShieldEntityAttributesObj = {};
            piiShieldConfigEntitiesAttributesObj[piiShieldEntityAttributes.entityType] = piiShieldEntityAttributesObj;
            const tasks = integrationTasks.filter((integrationTask: IntegrationTask) =>
                piiShieldEntityAttributes.entityType === integrationTask[`${eduToneDS}Type`]);

            piiShieldEntityAttributes.attributes = piiShieldEntityAttributes.attributes
                .map((attribute: PIIShieldAttribute) => {
                    let result = attribute;
                    if (!piiShieldEntityAttributesObj[attribute.propertyName]) {

                        let mapping: IntegrationTaskMap;
                        tasks.some((task: IntegrationTask) => {
                            mapping = (task?.mappings || []).find((taskMap: IntegrationTaskMap) =>
                                attribute.propertyName === taskMap[`${eduToneDS}PropertyName`]);
                            return mapping;
                        });

                        if (mapping) {
                            piiShieldEntityAttributesObj[attribute.propertyName] = {...mapping, ...{piiLevel: attribute.piiLevel}};
                        } else {
                            result = null;
                        }
                    }

                    return result;
                });
        });

        piiShieldConfigEntitiesAttributes.forEach((piiShieldEntityAttributes: PIIShieldEntityAttributes) => {
            piiShieldEntityAttributes.attributes = piiShieldEntityAttributes.attributes
                .filter((attribute: PIIShieldAttribute) => attribute);
        });

        return [piiShieldConfigEntitiesAttributesObj, piiShieldConfigEntitiesAttributes];
    }

    static calculatePIILevel(
        piiShieldEntitiesAttributes: PIIShieldEntityAttributes[],
        piiShieldEntitiesAttributesObj: object
    ): PII_LEVEL {
        if (piiShieldEntitiesAttributes?.length > 0) {
            const currentPIIShieldLevelsObj = {
                [PII_LEVEL.one]: null,
                [PII_LEVEL.two]: null,
                [PII_LEVEL.three]: null
            };

            for (let piiShieldEntityAttributes of piiShieldEntitiesAttributes) {

                piiShieldEntityAttributes = <PIIShieldEntityAttributes>piiShieldEntityAttributes;
                const piiShieldEntityAttributesObj = piiShieldEntitiesAttributesObj[piiShieldEntityAttributes.entityType];

                for (let attribute of piiShieldEntityAttributes.attributes) {

                    attribute = <PIIShieldAttribute>attribute;
                    const mapping: IntegrationTaskMap = piiShieldEntityAttributesObj[attribute.propertyName];

                    if ((currentPIIShieldLevelsObj[attribute.piiLevel] === true)
                        || (currentPIIShieldLevelsObj[attribute.piiLevel] === null)) {
                        currentPIIShieldLevelsObj[attribute.piiLevel] = mapping.piiShieldAction === PIIShieldAction.IS_SUPPORT;
                    }

                    if (currentPIIShieldLevelsObj[PII_LEVEL.one] === false) {
                        break;
                    }
                }

                if (currentPIIShieldLevelsObj[PII_LEVEL.one] === false) {
                    break;
                }
            }

            const isLevelOneReached = currentPIIShieldLevelsObj[PII_LEVEL.one];
            const isLevelTwoReached = currentPIIShieldLevelsObj[PII_LEVEL.two] && isLevelOneReached;
            const isLevelThreeReached = currentPIIShieldLevelsObj[PII_LEVEL.three] && isLevelTwoReached;

            return isLevelThreeReached
                ? PII_LEVEL.three
                : isLevelTwoReached
                    ? PII_LEVEL.two
                    : isLevelOneReached
                        ? PII_LEVEL.one
                        : PII_LEVEL.null;
        }
    }

    getPIIShieldLevelsConfig(): Observable<PIIShieldLevelConfig[]> {
        return this.get(`${environment.datahubBaseURI}/api/v2/applications/piiLevels/config`)
            .pipe(
                catchError(error => this.handleError(error, this.GET_PII_SHIELD_CONFIG_ERROR_MESSAGE)),
                takeUntil(this.store.pipe(select(isAuthenticated), filter(isAuthenticated => !isAuthenticated)))
            );
    }

    public getDistrictIntegrationsPIILevels(orgGuid: string): Observable<{ [key: string]: PII_LEVEL }> {
        const url = `${environment.datahubBaseURI}/api/v2/applications/piiLevels/integrations?districtGuid=${orgGuid}`;

        return this.get(url)
            .pipe(
                catchError(error => this.handleError(error, MESSAGES.UNKNOWN_ERROR)),
                takeUntil(this.store.pipe(select(isAuthenticated), filter(isAuthenticated => !isAuthenticated)))
            );
    }

    public getIntegrationPIILevel(integrationId: string): Observable<PII_LEVEL> {
        const url = `${environment.datahubBaseURI}/api/v2/applications/piiLevels/integrations/${integrationId}`;

        return this.get(url)
            .pipe(
                catchError(error => this.handleError(error, MESSAGES.UNKNOWN_ERROR)),
                takeUntil(this.store.pipe(select(isAuthenticated), filter(isAuthenticated => !isAuthenticated)))
            );
    }

    public getApplicationPIILevel(applicationGuid: string): Observable<PII_LEVEL> {
        const url = `${environment.datahubBaseURI}/api/v2/applications/piiLevels/applications/${applicationGuid}`;

        return this.get(url)
            .pipe(
                catchError(error => this.handleError(error, MESSAGES.UNKNOWN_ERROR)),
                takeUntil(this.store.pipe(select(isAuthenticated), filter(isAuthenticated => !isAuthenticated)))
            );
    }

    public getApplicationsPIILevels(): Observable<{ [key: string]: PIIShieldApplicationLevel }> {
        const url = `${environment.datahubBaseURI}/api/v2/applications/piiLevels/applications`;

        return this.get(url)
            .pipe(
                catchError(error => this.handleError(error, MESSAGES.UNKNOWN_ERROR)),
                takeUntil(this.store.pipe(select(isAuthenticated), filter(isAuthenticated => !isAuthenticated)))
            );
    }

    public regenerateApplicationPIIShieldLevel(integrationId: string): Observable<PII_LEVEL> {
        const url = `${environment.datahubBaseURI}/api/v2/applications/piiLevels/applications/regenerate?integrationId=${integrationId}`;

        return this.post(url)
            .pipe(
                catchError(error => this.handleError(error, MESSAGES.UNKNOWN_ERROR)),
                takeUntil(this.store.pipe(select(isAuthenticated), filter(isAuthenticated => !isAuthenticated)))
            );
    }

    public regenerateIntegrationsPIIShieldLevels(): Observable<PII_LEVEL> {
        const url = `${environment.datahubBaseURI}/api/v2/applications/piiLevels/integrations/regenerate`;

        return this.post(url)
            .pipe(
                catchError(error => this.handleError(error, MESSAGES.UNKNOWN_ERROR)),
                takeUntil(this.store.pipe(select(isAuthenticated), filter(isAuthenticated => !isAuthenticated)))
            );
    }

    public regenerateApplicationsPIIShieldLevels(): Observable<PII_LEVEL> {
        const url = `${environment.datahubBaseURI}/api/v2/applications/piiLevels/applications/regenerate`;

        return this.post(url)
            .pipe(
                catchError(error => this.handleError(error, MESSAGES.UNKNOWN_ERROR)),
                takeUntil(this.store.pipe(select(isAuthenticated), filter(isAuthenticated => !isAuthenticated)))
            );
    }

    public getVendorApplicationsPIILevel(vendorGuid: string): Observable<{ [key: string]: PII_LEVEL }> {
        const url = `${environment.datahubBaseURI}/api/v2/applications/piiLevels/applications/vendors/${vendorGuid}`;

        return this.get(url)
            .pipe(
                catchError(error => this.handleError(error, MESSAGES.UNKNOWN_ERROR)),
                takeUntil(this.store.pipe(select(isAuthenticated), filter(isAuthenticated => !isAuthenticated)))
            );
    }
}
